import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SensorConfig, SensorType } from '@/lib/sensor-types';
import { sensorManager } from '@/lib/sensor-integration';

interface SensorConnectionStatusProps {
  onSensorConnected: (config: SensorConfig) => void;
}

export const SensorConnectionStatus: React.FC<SensorConnectionStatusProps> = ({
  onSensorConnected
}) => {
  const [connectedSensors, setConnectedSensors] = useState<SensorConfig[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);

  const defaultSensors: Partial<SensorConfig>[] = [
    {
      id: 'heart_rate',
      type: SensorType.HEART_RATE,
      name: 'Heart Rate Monitor',
      sampleRate: 100
    },
    {
      id: 'eeg',
      type: SensorType.EEG,
      name: 'EEG Headset',
      sampleRate: 256
    },
    {
      id: 'gsr',
      type: SensorType.GSR,
      name: 'GSR Sensor',
      sampleRate: 50
    }
  ];

  const scanForSensors = async () => {
    setIsScanning(true);
    setScanProgress(0);

    // Simulate scanning process
    for (let i = 0; i <= 100; i += 10) {
      setScanProgress(i);
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // Simulate finding sensors
    const foundSensors: SensorConfig[] = defaultSensors.map(sensor => ({
      ...sensor,
      id: sensor.id!,
      type: sensor.type!,
      name: sensor.name!,
      sampleRate: sensor.sampleRate!,
      calibrated: false,
      connected: false,
      sensitivity: 0.5
    }));

    setConnectedSensors(foundSensors);
    setIsScanning(false);
  };

  const connectSensor = async (sensorConfig: SensorConfig) => {
    const success = await sensorManager.connectSensor(sensorConfig);
    if (success) {
      setConnectedSensors(prev => 
        prev.map(s => s.id === sensorConfig.id ? { ...s, connected: true } : s)
      );
      onSensorConnected(sensorConfig);
    }
  };

  const getStatusColor = (sensor: SensorConfig) => {
    if (sensor.connected && sensor.calibrated) return 'bg-green-500';
    if (sensor.connected) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusText = (sensor: SensorConfig) => {
    if (sensor.connected && sensor.calibrated) return 'Ready';
    if (sensor.connected) return 'Connected';
    return 'Disconnected';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Sensor Hardware Status
          <Button 
            onClick={scanForSensors} 
            disabled={isScanning}
            size="sm"
          >
            {isScanning ? 'Scanning...' : 'Scan Devices'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isScanning && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Scanning for devices...</span>
              <span>{scanProgress}%</span>
            </div>
            <Progress value={scanProgress} />
          </div>
        )}

        {connectedSensors.length === 0 && !isScanning && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-lg mb-2">No sensors detected</div>
            <div className="text-sm">Click "Scan Devices" to search for hardware</div>
          </div>
        )}

        <div className="space-y-3">
          {connectedSensors.map(sensor => (
            <div
              key={sensor.id}
              className="flex items-center justify-between p-4 border rounded-lg bg-white"
            >
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(sensor)}`} />
                <div>
                  <div className="font-medium">{sensor.name}</div>
                  <div className="text-sm text-gray-500">
                    {sensor.sampleRate}Hz • {sensor.type}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Badge variant={sensor.connected ? "default" : "secondary"}>
                  {getStatusText(sensor)}
                </Badge>
                {!sensor.connected && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => connectSensor(sensor)}
                  >
                    Connect
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {connectedSensors.some(s => s.connected) && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="text-sm font-medium text-blue-800 mb-2">
              Connection Status Summary
            </div>
            <div className="text-sm text-blue-600">
              {connectedSensors.filter(s => s.connected).length} of {connectedSensors.length} sensors connected
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};